import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Boxes, CalendarDays, Camera, LogOut, Hexagon, Droplets, ShieldCheck, UserCheck, X, Settings as SettingsIcon, Sparkles, BookOpen, MessagesSquare, GitCompare, CalendarClock, Users, History, FileText, Activity, Inbox, BarChart3, Gamepad2, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import NotificationsBell from "@/components/NotificationsBell";
import UpgradeModal from "@/components/UpgradeModal";
import api, { onUsageLimitHit } from "@/lib/api";
import useSupportUnreadCount from "@/hooks/useSupportUnreadCount";

const links = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/getting-started", label: "Getting started", icon: Sparkles, testid: "nav-getting-started", onlyIfOnboarding: true },
  { to: "/app/hives", label: "Hives", icon: Boxes, testid: "nav-hives" },
  { to: "/app/events", label: "Reminders", icon: CalendarDays, testid: "nav-events" },
  { to: "/app/smart-calendar", label: "Smart calendar", icon: CalendarClock, testid: "nav-smart-calendar" },
  { to: "/app/harvests", label: "Harvests", icon: Droplets, testid: "nav-harvests" },
  { to: "/app/diagnose", label: "Diagnose", icon: Camera, testid: "nav-diagnose" },
  { to: "/app/compare", label: "Compare", icon: GitCompare, testid: "nav-compare" },
  { to: "/app/games", label: "Games", icon: Gamepad2, testid: "nav-games", adminOnly: true },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/app/guide", label: "Guide", icon: BookOpen, testid: "nav-guide" },
  { to: "/forum", label: "Community", icon: MessagesSquare, testid: "nav-forum" },
];

export default function Layout() {
  const { user, logout, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [upgrade, setUpgrade] = useState({ open: false, detail: null });
  const [plan, setPlan] = useState(null);
  const [onboardingVisible, setOnboardingVisible] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Subscribe to global 402 → upgrade modal
  useEffect(() => {
    const off = onUsageLimitHit((detail) => setUpgrade({ open: true, detail }));
    return off;
  }, []);

  // Load onboarding status to decide whether the "Getting started" link
  // should appear in the sidebar. Hidden when everything is done or when
  // the user has explicitly dismissed it.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchOnboarding = () => {
      api.get("/engagement/onboarding")
        .then((r) => {
          if (cancelled) return;
          const o = r.data || {};
          const done = (o.completed ?? 0) >= (o.total ?? 0) && (o.total ?? 0) > 0;
          setOnboardingVisible(!o.dismissed && !done);
        })
        .catch(() => {});
    };
    fetchOnboarding();
    const onUpdate = () => fetchOnboarding();
    window.addEventListener("onboarding:updated", onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("onboarding:updated", onUpdate);
    };
  }, [user?.id]);

  // Load subscription to decide whether to show the upgrade pill
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchPlan = () => {
      api.get("/billing/subscription")
        .then((r) => { if (!cancelled) setPlan(r.data); })
        .catch(() => {});
    };
    fetchPlan();
    // Refetch when BillingTab signals a checkout success so the pill hides instantly.
    const onBillingUpdate = () => fetchPlan();
    window.addEventListener("billing:updated", onBillingUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("billing:updated", onBillingUpdate);
    };
  }, [user?.id]);

  const showUpgradePill = plan && plan.plan === "free";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
      toast.success("Returned to admin account");
      navigate("/app/admin");
    } catch (e) {
      toast.error("Could not switch back");
    }
  };

  return (
    <div className="min-h-screen honeycomb-bg">
      {user?.impersonator && (
        <div className="sticky top-0 z-40 bg-amber-200 border-b border-amber-300 text-amber-950" data-testid="impersonation-banner">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <UserCheck className="w-4 h-4" />
              <span>
                Viewing as <strong>{user.name}</strong> ({user.email}) · impersonation by{" "}
                <strong>{user.impersonator.email}</strong> · expires in 1h
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full bg-white border-amber-400 text-amber-900 hover:bg-amber-50 h-7 px-3"
              onClick={handleStopImpersonation}
              data-testid="stop-impersonation-button"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Return to admin
            </Button>
          </div>
        </div>
      )}
      <div className="flex">
        <aside className="w-64 shrink-0 border-r border-stone-200 bg-white min-h-screen sticky top-0 hidden md:flex flex-col">
          <SidebarInner
            user={user}
            onboardingVisible={onboardingVisible}
            showUpgradePill={showUpgradePill}
            navigate={navigate}
            onLogout={handleLogout}
          />
        </aside>

        <main className="flex-1 min-w-0">
          <header className="md:hidden flex items-center justify-between h-14 px-4 border-b border-stone-200 bg-white">
            <div className="flex items-center gap-2">
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <button
                    className="p-1.5 -ml-1 rounded-md text-stone-700 hover:bg-stone-100"
                    aria-label="Open menu"
                    data-testid="mobile-menu-button"
                  >
                    <Menu className="w-6 h-6" />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-72 flex flex-col bg-white" data-testid="mobile-nav-drawer">
                  <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                  <SidebarInner
                    user={user}
                    onboardingVisible={onboardingVisible}
                    showUpgradePill={showUpgradePill}
                    navigate={navigate}
                    onLogout={handleLogout}
                    onNavigate={() => setMobileNavOpen(false)}
                  />
                </SheetContent>
              </Sheet>
              <Link to="/app" className="flex items-center gap-2" onClick={() => setMobileNavOpen(false)}>
                <Hexagon className="w-5 h-5 text-yellow-500" />
                <span className="font-display font-semibold">Hive <span className="text-yellow-500">@</span> Buzzzillion</span>
              </Link>
            </div>
            <div className="flex items-center gap-1">
              <NotificationsBell />
            </div>
          </header>
          <div className="hidden md:flex items-center justify-end gap-2 h-12 px-6 border-b border-stone-100">
            <NotificationsBell />
          </div>
          <div className="p-6 md:p-10 max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
      <UpgradeModal
        open={upgrade.open}
        detail={upgrade.detail}
        onClose={() => setUpgrade({ open: false, detail: null })}
      />
    </div>
  );
}

function SidebarInner({ user, onboardingVisible, showUpgradePill, navigate, onLogout, onNavigate }) {
  const linkClass = (active, admin = false) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm transition-colors ${
      active
        ? admin
          ? "bg-amber-200 text-amber-900 font-medium"
          : "bg-yellow-100 text-amber-900 font-medium"
        : admin
        ? "text-amber-800 hover:bg-amber-50"
        : "text-stone-600 hover:bg-stone-100"
    }`;
  return (
    <>
      <Link to="/app" onClick={onNavigate} className="flex items-center gap-2 px-6 h-16 border-b border-stone-200 shrink-0">
        <div className="w-8 h-8 rounded-lg bg-yellow-400 grid place-items-center">
          <Hexagon className="w-5 h-5 text-amber-900" strokeWidth={2.5} />
        </div>
        <span className="font-display text-xl font-semibold tracking-tight">Hive <span className="text-yellow-500">@</span> Buzzzillion</span>
      </Link>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {links
          .filter((l) => (!l.onlyIfOnboarding || onboardingVisible) && (!l.adminOnly || user?.is_admin))
          .map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testid} onClick={onNavigate} className={({ isActive }) => linkClass(isActive)}>
              <l.icon className="w-4 h-4" />
              {l.label}
            </NavLink>
          ))}
        {user?.is_admin && (
          <>
            <NavLink to="/app/admin" data-testid="nav-admin" end onClick={onNavigate} className={({ isActive }) => linkClass(isActive, true)}>
              <ShieldCheck className="w-4 h-4" />
              <span className="flex-1">Admin</span>
              <SupportBadge />
            </NavLink>
            <AdminSubNav onNavigate={onNavigate} />
          </>
        )}
      </nav>
      <div className="p-4 border-t border-stone-200 shrink-0">
        {showUpgradePill && (
          <button
            onClick={() => { onNavigate?.(); navigate("/app/settings?tab=billing"); }}
            className="w-full mb-3 group relative overflow-hidden rounded-full px-4 py-2.5 text-sm font-semibold text-stone-950 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 hover:from-amber-300 hover:to-amber-300 shadow-[0_0_24px_rgba(245,158,11,0.45)] transition-all flex items-center justify-center gap-2"
            data-testid="upgrade-to-pro-pill"
          >
            <Sparkles className="w-4 h-4" />
            <span>Upgrade to Pro</span>
          </button>
        )}
        <div className="px-3 py-2 text-xs text-stone-500">
          <div className="font-medium text-stone-800 truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="truncate">{user?.email}</div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-stone-600 hover:text-stone-900 rounded-full"
          onClick={onLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign out
        </Button>
      </div>
    </>
  );
}

const ADMIN_SUB_LINKS = [
  { hash: "admin-support",      label: "Support inbox",  icon: Inbox,    testid: "nav-admin-support" },
  { hash: "admin-stats",        label: "Overview",       icon: BarChart3, testid: "nav-admin-stats" },
  { hash: "admin-analytics",    label: "Analytics",      icon: Activity, testid: "nav-admin-analytics" },
  { hash: "admin-health",       label: "System health",  icon: Activity, testid: "nav-admin-health" },
  { hash: "admin-users",        label: "Users",          icon: Users,    testid: "nav-admin-users" },
  { hash: "admin-audit",        label: "Activity log",   icon: History,  testid: "nav-admin-audit" },
  { hash: "admin-guide",        label: "Guide articles", icon: BookOpen, testid: "nav-admin-guide" },
  { hash: "admin-cms",          label: "Content (CMS)",  icon: FileText, testid: "nav-admin-cms" },
];

function AdminSubNav({ onNavigate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const onAdmin = location.pathname.startsWith("/app/admin");
  const { user } = useAuth();
  const { unread } = useSupportUnreadCount(!!user?.is_admin);
  if (!onAdmin) return null;

  const jump = (e, hash) => {
    e.preventDefault();
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Update the URL so it's shareable / back-button-friendly
      navigate(`/app/admin#${hash}`, { replace: true });
    }
    onNavigate?.();
  };

  return (
    <ul className="ml-3 pl-3 border-l border-amber-200/70 space-y-0.5 mt-1 mb-1" data-testid="admin-sub-nav">
      {ADMIN_SUB_LINKS.map((l) => (
        <li key={l.hash}>
          <a
            href={`#${l.hash}`}
            onClick={(e) => jump(e, l.hash)}
            data-testid={l.testid}
            className="flex items-center gap-2.5 px-3 py-1.5 rounded-full text-[13px] text-amber-800 hover:bg-amber-50 transition-colors"
          >
            <l.icon className="w-3.5 h-3.5" />
            <span className="flex-1">{l.label}</span>
            {l.hash === "admin-support" && unread > 0 && (
              <span
                className="text-[10px] font-bold tabular-nums leading-none px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-rose-600 text-white"
                data-testid="support-unread-badge"
                aria-label={`${unread} unread support requests`}
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </a>
        </li>
      ))}
    </ul>
  );
}

function SupportBadge() {
  const { user } = useAuth();
  const { unread } = useSupportUnreadCount(!!user?.is_admin);
  if (!unread) return null;
  return (
    <span
      className="text-[10px] font-bold tabular-nums leading-none px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-rose-600 text-white"
      data-testid="admin-support-badge"
      aria-label={`${unread} unread support requests`}
    >
      {unread > 99 ? "99+" : unread}
    </span>
  );
}
